/**
 * Clarisで使用される数値文字列を安全に数値に変換する関数
 *
 * この関数は以下の文字を自動的に除外します：
 * - カンマ (,)
 * - パーセント (%)
 * - アスタリスク (*)
 * - 半角スペース ( )
 * - 全角スペース (U+3000)
 * - プラス記号 (+)
 * - その他の不要な文字
 *
 * @param value - 変換する文字列、数値、またはundefined/null/DataKey
 * @returns 変換された数値、変換できない場合はNaN
 *
 * @example
 * parseNumber("1,000") // 1000
 * parseNumber("50%") // 50
 * parseNumber("1,234.56") // 1234.56
 * parseNumber("\u3000 1,000 \u3000") // 1000
 * parseNumber("+500") // 500
 * parseNumber("invalid") // NaN
 * parseNumber(undefined) // NaN
 * parseNumber(null) // 0
 */
export const parseNumber = (
  value: string | number | undefined | null | ((obj: unknown) => unknown)
): number => {
  // undefinedの場合はNaNを返す
  if (value === undefined) {
    return NaN;
  }

  // nullの場合は0を返す
  if (value === null) {
    return 0;
  }

  // 関数の場合はNaNを返す（DataKey<any>の場合は関数として渡されることがある）
  if (typeof value === 'function') {
    return NaN;
  }

  // 既に数値の場合はそのまま返す
  if (typeof value === 'number') {
    return value;
  }

  // 文字列でない場合はNaNを返す
  if (typeof value !== 'string') {
    return NaN;
  }

  // 空文字列の場合は0を返す
  if (value.trim() === '') {
    return 0;
  }

  // Clarisで使用される不要な文字を除外
  // カンマ、パーセント、アスタリスク、半角・全角スペース、プラス記号を削除
  const cleanedValue = value
    .replace(/[,%*\s\u3000+]/g, '') // 半角スペース、全角スペース(U+3000)、カンマ、パーセント、アスタリスク、プラスを削除
    .trim();

  // 空になった場合はNaNを返す
  if (cleanedValue === '') {
    return NaN;
  }

  // 数値に変換
  // eslint-disable-next-line no-direct-number
  const result = Number(cleanedValue);

  return result;
};

/**
 * parseNumberのエイリアス（後方互換性のため）
 * @deprecated parseNumberを使用してください
 */
export const safeParseNumber = parseNumber;
